import { useEffect, useId, useRef, useState } from "react";
import { fetchCountries } from "@/lib/countries";
import { countryLabel } from "@/lib/employeeRecordLabels";
import type { Drzava } from "@/lib/radnici";
import { Input } from "./input";
import "./CountryCombobox.css";

const preferred = ["HR", "BA", "RS", "XK", "MK", "AL", "ME", "UA", "NP", "IN", "PH"];
const normalize = (text: string) => text.normalize("NFD").replace(/\p{M}/gu, "").toLocaleLowerCase("hr");

/** Search text is local; only an explicit catalogue selection changes the code. */
export function CountryCombobox({ id, label, value, onChange, disabled = false, required = false, error, prescribed = false, valueFormat = "code" }: {
  id?: string; label: string; value: string; onChange: (code: string) => void;
  disabled?: boolean; required?: boolean; error?: string; prescribed?: boolean;
  valueFormat?: "code" | "name";
}) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const listId = `${inputId}-options`;
  const [countries, setCountries] = useState<Drzava[]>([]);
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");
  const [revision, setRevision] = useState(0);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const controller = new AbortController();
    fetchCountries(controller.signal).then(items => {
      if (controller.signal.aborted) return;
      setCountries([...items].sort((a, b) => {
        const rank = (code: string) => preferred.includes(code) ? preferred.indexOf(code) : preferred.length;
        return rank(a.iso2) - rank(b.iso2) || a.naziv.localeCompare(b.naziv, "hr");
      }));
      setStatus("ready");
    }).catch(() => { if (!controller.signal.aborted) setStatus("error"); });
    return () => controller.abort();
  }, [revision]);
  const options = countries.filter(country => normalize(`${country.naziv} ${country.iso2}`).includes(normalize(query.trim())));
  const index = Math.min(active, Math.max(0, options.length - 1));
  useEffect(() => {
    const list = listRef.current;
    const option = list?.children[index] as HTMLElement | undefined;
    if (!list || !option) return;
    if (option.offsetTop < list.scrollTop) list.scrollTop = option.offsetTop;
    else if (option.offsetTop + option.offsetHeight > list.scrollTop + list.clientHeight) list.scrollTop = option.offsetTop + option.offsetHeight - list.clientHeight;
  }, [index, open, query]);
  function close() { setOpen(false); setQuery(""); }
  function choose(code: string) {
    if (disabled || rootRef.current?.querySelector("input")?.matches(":disabled")) return;
    onChange(valueFormat === "name" && code ? countries.find(country => country.iso2 === code)!.naziv : code); close();
  }
  function show() { setOpen(true); setQuery(""); setActive(0); }
  const selected = countries.find(country => country.iso2 === value)?.naziv ?? countryLabel(value) ?? "";
  return <div ref={rootRef} className="avm-country-picker flex min-w-0 flex-col gap-2">
    <label htmlFor={inputId} className="oznaka">{label}{(required || prescribed) && <span aria-hidden="true"> *</span>}</label>
    <div className="relative min-w-0">
      <Input id={inputId} role="combobox" aria-autocomplete="list" aria-expanded={open} aria-controls={open ? listId : undefined}
        aria-activedescendant={open && options[index] ? `${listId}-${index}` : undefined} aria-required={required} aria-invalid={!!error}
        aria-describedby={`${inputId}-status`} data-required-empty={(required || prescribed) && !value}
        disabled={disabled} autoComplete="off" className="min-h-11 pr-12" placeholder="Odaberite ili pretražite državu"
        value={open ? query : selected} onFocus={show} onClick={() => { if (!open) show(); }} onBlur={close}
        onChange={event => { setQuery(event.target.value); setOpen(true); setActive(0); }}
        onKeyDown={event => {
          if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault(); event.stopPropagation();
            if (!open) show(); else setActive(Math.max(0, Math.min(options.length - 1, index + (event.key === "ArrowDown" ? 1 : -1))));
          } else if (event.key === "Enter" && open) {
            event.preventDefault(); event.stopPropagation(); if (options[index]) choose(options[index].iso2);
          } else if (event.key === "Escape" && open) { event.preventDefault(); event.stopPropagation(); close(); }
        }} />
      {!!value && !required && <button type="button" disabled={disabled} aria-label={`Ukloni odabir: ${label}`} className="absolute right-0 top-0 flex min-h-11 min-w-11 items-center justify-center" onPointerDown={event => event.preventDefault()} onClick={() => choose("")}>×</button>}
      {(!value || required) && <span aria-hidden="true" className="pointer-events-none absolute right-0 top-0 flex min-h-11 min-w-11 items-center justify-center">⌄</span>}
      {open && <ul ref={listRef} id={listId} role="listbox" aria-label={label} className="absolute z-40 mt-1 max-h-60 w-full overflow-y-auto overscroll-contain rounded border border-[var(--color-border)] bg-[var(--color-card)] p-1 shadow-lg">
        {options.map((country, position) => <li id={`${listId}-${position}`} key={country.iso2} role="option" aria-selected={position === index}
          className={`flex min-h-11 cursor-pointer items-center rounded px-3 py-2 ${position === index ? "bg-[var(--color-muted)]" : ""}`}
          onPointerDown={event => event.preventDefault()} onClick={() => choose(country.iso2)}>{country.naziv}</li>)}
        {status === "ready" && !options.length && <li role="status" className="p-3">Nema pronađenih država.</li>}
      </ul>}
    </div>
    <div id={`${inputId}-status`} className="text-sm">
      {error && <p role="alert" className="text-destructive">{error}</p>}
      {status === "loading" && <p role="status">Učitavanje država…</p>}
      {status === "error" && <div role="alert">Popis država nije dostupan. <button type="button" disabled={disabled} className="min-h-11 underline" onClick={() => { setStatus("loading"); setRevision(value => value + 1); }}>Pokušaj ponovno</button></div>}
    </div>
  </div>;
}
